import { useEffect, useState } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge } from "../components/Shared";
import { toast } from "sonner";
import { FloppyDisk, ArrowsClockwise } from "@phosphor-icons/react";

export default function Settings() {
  const [config, setConfig] = useState(null);
  const [models, setModels] = useState({ openai: [], anthropic: [] });
  const [agents, setAgents] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [{ data: cfg }, { data: ms }, { data: ag }] = await Promise.all([
      api.get("/system/config"),
      api.get("/agents/models"),
      api.get("/agents"),
    ]);
    setConfig(cfg);
    setModels(ms);
    setAgents(Array.isArray(ag) ? ag : []);
  };

  useEffect(() => { load(); }, []);

  if (!config) return <div className="p-10 text-carbon-500">Cargando...</div>;

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        default_provider: config.default_provider,
        default_model: config.default_model,
        whatsapp_provider: config.whatsapp_provider,
      };
      if (config.openai_api_key && !config.openai_api_key.startsWith("••")) payload.openai_api_key = config.openai_api_key;
      if (config.anthropic_api_key && !config.anthropic_api_key.startsWith("••")) payload.anthropic_api_key = config.anthropic_api_key;
      if (config.dialog360_api_key && !config.dialog360_api_key.startsWith("••")) payload.dialog360_api_key = config.dialog360_api_key;
      if (config.dialog360_phone_number_id) payload.dialog360_phone_number_id = config.dialog360_phone_number_id;
      if (config.frontend_base_url !== undefined) payload.frontend_base_url = (config.frontend_base_url || "").trim() || null;
      if (config.community_whatsapp_number !== undefined) payload.community_whatsapp_number = (config.community_whatsapp_number || "").trim() || null;
      if (config.hm_app_url !== undefined) payload.hm_app_url = (config.hm_app_url || "").trim() || null;
      if (config.landing_logo_url !== undefined) payload.landing_logo_url = (config.landing_logo_url || "").trim() || null;
      if (config.v2_followup_template !== undefined) payload.v2_followup_template = (config.v2_followup_template || "").trim() || null;
      if (config.v2_followup_template_lang !== undefined) payload.v2_followup_template_lang = (config.v2_followup_template_lang || "").trim() || null;
      if (config.appointment_reminder_24h_template !== undefined) payload.appointment_reminder_24h_template = (config.appointment_reminder_24h_template || "").trim() || null;
      if (config.appointment_reminder_1h_template !== undefined) payload.appointment_reminder_1h_template = (config.appointment_reminder_1h_template || "").trim() || null;
      if (config.appointment_reminder_template_lang !== undefined) payload.appointment_reminder_template_lang = (config.appointment_reminder_template_lang || "").trim() || null;
      if (config.first_contact_delay_seconds !== undefined && config.first_contact_delay_seconds !== "") {
        payload.first_contact_delay_seconds = parseInt(config.first_contact_delay_seconds, 10);
      }
      if (config.bot_response_delay_seconds !== undefined && config.bot_response_delay_seconds !== "") {
        payload.bot_response_delay_seconds = parseInt(config.bot_response_delay_seconds, 10);
      }
      if (config.outbound_message_delay_seconds !== undefined && config.outbound_message_delay_seconds !== "") {
        payload.outbound_message_delay_seconds = parseFloat(config.outbound_message_delay_seconds);
      }
      if (config.inbound_message_buffer_seconds !== undefined && config.inbound_message_buffer_seconds !== "") {
        payload.inbound_message_buffer_seconds = parseFloat(config.inbound_message_buffer_seconds);
      }
      if (config.escalation_monitor_threshold !== undefined && config.escalation_monitor_threshold !== "") {
        payload.escalation_monitor_threshold = parseInt(config.escalation_monitor_threshold, 10);
      }
      if (config.escalation_human_threshold !== undefined && config.escalation_human_threshold !== "") {
        payload.escalation_human_threshold = parseInt(config.escalation_human_threshold, 10);
      }
      payload.auto_evidence_extraction_enabled = !!config.auto_evidence_extraction_enabled;
      payload.relay_agent_enabled = !!config.relay_agent_enabled;
      if (config.relay_agent_id !== undefined) payload.relay_agent_id = config.relay_agent_id || null;
      await api.patch("/system/config", payload);
      toast.success("Configuración guardada");
      load();
    } catch (err) { toast.error("Error guardando"); } finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader
        label="Sistema"
        title="Configuración"
        description="LLM defaults, proveedor de WhatsApp."
      />
      <PageBody>
        <div className="space-y-6 max-w-3xl">
          <Card className="p-6">
            <div className="section-label mb-2">LLM</div>
            <h2 className="font-heading text-xl mb-4">Configuración de IA</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <label className="block"><span className="section-label">Provider por defecto</span>
                <select value={config.default_provider} onChange={(e) => setConfig({ ...config, default_provider: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                </select></label>
              <label className="block"><span className="section-label">Modelo por defecto</span>
                <select value={config.default_model} onChange={(e) => setConfig({ ...config, default_model: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono">
                  {(models[config.default_provider] || []).map((m) => <option key={m} value={m}>{m}</option>)}
                </select></label>
            </div>
            <label className="block mb-3"><span className="section-label">OpenAI API key (del tenant)</span>
              <input value={config.openai_api_key || ""} onChange={(e) => setConfig({ ...config, openai_api_key: e.target.value })} data-testid="openai-key-input" placeholder="sk-..." className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" /></label>
            <label className="block"><span className="section-label">Anthropic API key (del tenant)</span>
              <input value={config.anthropic_api_key || ""} onChange={(e) => setConfig({ ...config, anthropic_api_key: e.target.value })} placeholder="sk-ant-..." className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" /></label>
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">WhatsApp</div>
            <h2 className="font-heading text-xl mb-4">Provider</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <label className="block"><span className="section-label">Provider activo</span>
                <select value={config.whatsapp_provider} onChange={(e) => setConfig({ ...config, whatsapp_provider: e.target.value })} data-testid="wa-provider-select" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                  <option value="mock">MockWhatsAppProvider (demo)</option>
                  <option value="dialog360">Dialog360Provider (producción)</option>
                </select></label>
            </div>
            <label className="block mb-3"><span className="section-label">Dialog360 API key</span>
              <input value={config.dialog360_api_key || ""} onChange={(e) => setConfig({ ...config, dialog360_api_key: e.target.value })} placeholder="••••" className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" /></label>
            <label className="block"><span className="section-label">Dialog360 Phone Number ID</span>
              <input value={config.dialog360_phone_number_id || ""} onChange={(e) => setConfig({ ...config, dialog360_phone_number_id: e.target.value })} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" /></label>
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">Calendario</div>
            <h2 className="font-heading text-xl mb-2">Base URL para links públicos</h2>
            <p className="text-sm text-carbon-500 mb-4">
              URL pública del frontend que se usará para construir los links de booking enviados al usuario.
              Ej: si pones <code className="text-terracotta">https://app.heroesmigrantes.com</code>, el link enviado al usuario
              será <code className="text-terracotta">https://app.heroesmigrantes.com/book/&#123;token&#125;</code>.
            </p>
            <label className="block">
              <span className="section-label">Frontend Base URL</span>
              <input
                value={config.frontend_base_url || ""}
                onChange={(e) => setConfig({ ...config, frontend_base_url: e.target.value })}
                data-testid="frontend-base-url-input"
                placeholder="https://app.heroesmigrantes.com"
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
              />
            </label>
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">Comunidad · Referidos</div>
            <h2 className="font-heading text-xl mb-2">Número público de WhatsApp</h2>
            <p className="text-sm text-carbon-500 mb-4">
              Número de WhatsApp donde nuevos referidos te van a escribir. Se inserta en el link <code className="text-terracotta">wa.me/&#123;número&#125;?text=...REF-HM-XXXXX</code>
              que la IA envía a fundadores confirmados/rechazados para que inviten a paisanos.
              Formato E.164 con código de país (ej. <code className="text-terracotta">+50239927035</code>). Si lo dejás vacío, la IA no insertará el link y no se podrá expandir la comunidad por referidos.
            </p>
            <label className="block">
              <span className="section-label">Community WhatsApp Number</span>
              <input
                value={config.community_whatsapp_number || ""}
                onChange={(e) => setConfig({ ...config, community_whatsapp_number: e.target.value })}
                data-testid="community-wa-number-input"
                placeholder="+50239927035"
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
              />
            </label>
            <div className="mt-5 pt-4 border-t border-carbon-200">
              <label className="block">
                <span className="section-label">URL de la App de Héroes Migrantes (Mini Landing)</span>
                <input
                  value={config.hm_app_url || ""}
                  onChange={(e) => setConfig({ ...config, hm_app_url: e.target.value })}
                  data-testid="hm-app-url-input"
                  placeholder="https://app.heroesmigrantes.com"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm"
                />
              </label>
              <label className="block mt-3">
                <span className="section-label">URL del logo oficial (Mini Landing)</span>
                <input
                  value={config.landing_logo_url || ""}
                  onChange={(e) => setConfig({ ...config, landing_logo_url: e.target.value })}
                  data-testid="landing-logo-url-input"
                  placeholder="https://…/logo-hm.png (vacío = emblema por defecto)"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm"
                />
              </label>
            </div>
          </Card>


          <Card className="p-6">
            <div className="section-label mb-2">Journey V2 · Seguimiento contextual</div>
            <h2 className="font-heading text-xl mb-2">Plantilla de seguimiento (fuera de 24h)</h2>
            <p className="text-sm text-carbon-500 mb-4">
              WhatsApp solo permite escribir texto libre dentro de las <strong>24 horas</strong> desde el último mensaje del Aspirante.
              Como los seguimientos suelen ocurrir después (ej. "mañana descargo la App"), fuera de esa ventana se
              <strong> debe usar una plantilla aprobada por Meta</strong>. Dentro de la ventana, Luciana responde con texto libre contextual.
              Crea la plantilla en 360dialog/Meta con estado <code className="text-terracotta">APPROVED</code> y una sola variable
              <code className="text-terracotta"> &#123;&#123;1&#125;&#125;</code> = nombre (ej. <em>"Hola &#123;&#123;1&#125;&#125;, queremos dar seguimiento a tu interés en Héroes Migrantes 🙌 ¿Continuamos? Responde por aquí."</em>).
              Escribe aquí el <strong>nombre exacto</strong> y el <strong>código de idioma</strong> de esa plantilla.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <label className="block">
                <span className="section-label">Nombre de la plantilla</span>
                <input
                  value={config.v2_followup_template || ""}
                  onChange={(e) => setConfig({ ...config, v2_followup_template: e.target.value })}
                  data-testid="v2-followup-template-input"
                  placeholder="seguimiento_aspirante"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
                />
              </label>
              <label className="block">
                <span className="section-label">Código de idioma</span>
                <input
                  value={config.v2_followup_template_lang || ""}
                  onChange={(e) => setConfig({ ...config, v2_followup_template_lang: e.target.value })}
                  data-testid="v2-followup-template-lang-input"
                  placeholder="es"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
                />
              </label>
            </div>
            {!config.v2_followup_template && (
              <div className="mt-3 text-xs text-signal">
                ⚠️ Sin plantilla configurada, los seguimientos que caigan fuera de la ventana de 24h quedarán <strong>en espera</strong> (no se envían) hasta que la configures.
              </div>
            )}
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">Conversación · Agente de relevo</div>
            <h2 className="font-heading text-xl mb-2">Agente conversacional de relevo</h2>
            <p className="text-sm text-carbon-500 mb-4">
              Mantiene la conversación "en caliente" en <strong>todos los pipelines</strong> cuando
              el lead cae en la <strong>Bandeja Humana</strong> y ningún humano la ha tomado, o cuando
              la IA del pipeline <strong>no generó respuesta</strong>. Se <strong>desactiva automáticamente</strong>
              en cuanto un humano <strong>toma</strong> la conversación. Responde solo desde la base de
              conocimiento del agente, <strong>sin tomar decisiones ni prometer</strong>, y avisa que una
              persona dará seguimiento. Configura su personalidad y conocimiento en la pantalla <strong>Agentes</strong>.
            </p>
            <label className="flex items-center gap-3 cursor-pointer mb-4" data-testid="relay-agent-toggle-label">
              <input
                type="checkbox"
                checked={!!config.relay_agent_enabled}
                onChange={(e) => setConfig({ ...config, relay_agent_enabled: e.target.checked })}
                data-testid="relay-agent-toggle"
                className="h-5 w-5 accent-terracotta"
              />
              <span className="text-sm">
                {config.relay_agent_enabled ? "Activado" : "Desactivado"}
              </span>
            </label>
            <label className="block max-w-md">
              <span className="section-label">Agente a usar como relevo</span>
              <select
                value={config.relay_agent_id || ""}
                onChange={(e) => setConfig({ ...config, relay_agent_id: e.target.value })}
                data-testid="relay-agent-select"
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm"
              >
                <option value="">— Selecciona un agente —</option>
                {agents.map((a) => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
            </label>
            {config.relay_agent_enabled && !config.relay_agent_id && (
              <div className="mt-3 text-xs text-signal">
                ⚠️ Activado pero sin agente seleccionado: no responderá hasta que elijas uno.
              </div>
            )}
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">Journey V2 · Expediente del Aspirante</div>
            <h2 className="font-heading text-xl mb-2">Extracción automática de evidencia por IA</h2>
            <p className="text-sm text-carbon-500 mb-4">
              Cuando está <strong>activada</strong>, la IA analiza automáticamente la conversación del Aspirante
              (Journey V2) y actualiza su Expediente — evidencia <strong>Declarado/Realizado</strong>, ideas,
              resumen y próximas acciones — <strong>después de cada mensaje</strong> del Aspirante, solo si hay
              al menos <strong>3 mensajes nuevos</strong> desde la última extracción (para controlar el costo de IA).
              Es idempotente: no duplica. La IA <strong>nunca</strong> marca Verificado (eso es humano/App HM).
              El botón manual <em>"Extraer evidencia con IA"</em> sigue disponible siempre.
            </p>
            <label className="flex items-center gap-3 cursor-pointer" data-testid="auto-evidence-toggle-label">
              <input
                type="checkbox"
                checked={!!config.auto_evidence_extraction_enabled}
                onChange={(e) => setConfig({ ...config, auto_evidence_extraction_enabled: e.target.checked })}
                data-testid="auto-evidence-extraction-toggle"
                className="h-5 w-5 accent-terracotta"
              />
              <span className="text-sm">
                {config.auto_evidence_extraction_enabled
                  ? "Activada — la IA extrae evidencia automáticamente"
                  : "Desactivada — solo extracción manual (botón)"}
              </span>
            </label>
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">Calendario · Recordatorios de cita</div>
            <h2 className="font-heading text-xl mb-2">Plantillas de recordatorio (24h / 1h)</h2>
            <p className="text-sm text-carbon-500 mb-4">
              Los recordatorios de sesión se envían normalmente <strong>fuera</strong> de la ventana de 24h, así que Meta exige
              <strong> plantilla aprobada</strong>. Crea las plantillas con dos variables: <code className="text-terracotta">&#123;&#123;1&#125;&#125;</code> = nombre y
              <code className="text-terracotta"> &#123;&#123;2&#125;&#125;</code> = fecha/hora (ej. <em>"Hola &#123;&#123;1&#125;&#125;, te recordamos tu sesión el &#123;&#123;2&#125;&#125;. ¡Te esperamos!"</em>).
              Si el lead respondió en las últimas 24h, se envía texto libre.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="block">
                <span className="section-label">Plantilla 24h (nombre)</span>
                <input
                  value={config.appointment_reminder_24h_template || ""}
                  onChange={(e) => setConfig({ ...config, appointment_reminder_24h_template: e.target.value })}
                  data-testid="appt-24h-template-input"
                  placeholder="recordatorio_sesion_24h"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
                />
              </label>
              <label className="block">
                <span className="section-label">Plantilla 1h (nombre)</span>
                <input
                  value={config.appointment_reminder_1h_template || ""}
                  onChange={(e) => setConfig({ ...config, appointment_reminder_1h_template: e.target.value })}
                  data-testid="appt-1h-template-input"
                  placeholder="recordatorio_sesion_1h"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
                />
              </label>
              <label className="block">
                <span className="section-label">Código de idioma</span>
                <input
                  value={config.appointment_reminder_template_lang || ""}
                  onChange={(e) => setConfig({ ...config, appointment_reminder_template_lang: e.target.value })}
                  data-testid="appt-template-lang-input"
                  placeholder="es"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
                />
              </label>
            </div>
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">Bot · Timing del primer contacto</div>
            <h2 className="font-heading text-xl mb-2">Delay del primer mensaje del bot al nuevo lead</h2>
            <p className="text-sm text-carbon-500 mb-4">
              Cuando entra un lead nuevo (por Google Sheets, webhook 360Dialog o creación manual),
              el bot espera este tiempo antes de mandarle el primer mensaje. Útil para evitar que
              parezca robotizado o pegado. Aplica a Founders y Ambassadors. Default <code className="text-terracotta">60s</code>.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="section-label">Delay (segundos)</span>
                <input
                  type="number" min="0" max="86400"
                  value={config.first_contact_delay_seconds ?? 60}
                  onChange={(e) => setConfig({ ...config, first_contact_delay_seconds: e.target.value })}
                  data-testid="first-contact-delay-input"
                  placeholder="60"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
                />
              </label>
              <div className="text-xs text-carbon-500 self-end mb-2">
                Ejemplos: <code className="text-terracotta">60</code> = 1 min · <code className="text-terracotta">300</code> = 5 min · <code className="text-terracotta">1800</code> = 30 min · <code className="text-terracotta">3600</code> = 1 hora.
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">Delay de cada respuesta del bot</div>
            <h2 className="font-heading text-xl mb-2">Tiempo entre respuestas IA</h2>
            <p className="text-sm text-carbon-500 mb-4">
              En CADA respuesta que el bot envía al lead (no solo la primera), espera estos segundos
              antes de enviar el WhatsApp. Simula tiempo de "tipeo" para que la conversación se sienta
              más natural. Default <code className="text-terracotta">0s</code> (sin delay). Máximo permitido: 120s.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="section-label">Delay por mensaje (segundos)</span>
                <input
                  type="number" min="0" max="120"
                  value={config.bot_response_delay_seconds ?? 0}
                  onChange={(e) => setConfig({ ...config, bot_response_delay_seconds: e.target.value })}
                  data-testid="bot-response-delay-input"
                  placeholder="0"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
                />
              </label>
              <div className="text-xs text-carbon-500 self-end mb-2">
                Ejemplos: <code className="text-terracotta">3</code> = 3 seg · <code className="text-terracotta">10</code> = 10 seg · <code className="text-terracotta">30</code> = 30 seg.
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">Bot · Delay entre mensajes divididos</div>
            <h2 className="font-heading text-xl mb-2">Delay entre mensajes de una misma respuesta</h2>
            <p className="text-sm text-carbon-500 mb-4">
              Cuando la IA divide su respuesta en varios mensajes (con marcadores <code className="text-terracotta">mensaje 1:</code>, <code className="text-terracotta">mensaje 2:</code>…),
              este es el tiempo que espera entre enviar cada parte. Evita que lleguen todos pegados.
              Default <code className="text-terracotta">3s</code>. Máximo permitido: 30s.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="section-label">Delay entre partes (segundos)</span>
                <input
                  type="number" min="0" max="30" step="0.5"
                  value={config.outbound_message_delay_seconds ?? 3}
                  onChange={(e) => setConfig({ ...config, outbound_message_delay_seconds: e.target.value })}
                  data-testid="outbound-message-delay-input"
                  placeholder="3"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
                />
              </label>
              <div className="text-xs text-carbon-500 self-end mb-2">
                Ejemplos: <code className="text-terracotta">3</code> = 3 seg · <code className="text-terracotta">1.5</code> = 1.5 seg · <code className="text-terracotta">0</code> = sin delay.
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">Bot · Buffer de mensajes entrantes</div>
            <h2 className="font-heading text-xl mb-2">Agrupar mensajes entrantes del lead (anti-spam)</h2>
            <p className="text-sm text-carbon-500 mb-4">
              Si el lead manda varios mensajes seguidos (ej: "hola" / "buenas" / "quiero info" en pocos segundos),
              el bot espera este tiempo, los agrupa y responde UNA sola vez con todo el contexto en vez de contestar
              cada mensaje por separado. Default <code className="text-terracotta">0s</code> (desactivado). Recomendado: 5–10s.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="section-label">Ventana de buffer (segundos)</span>
                <input
                  type="number" min="0" max="120" step="1"
                  value={config.inbound_message_buffer_seconds ?? 0}
                  onChange={(e) => setConfig({ ...config, inbound_message_buffer_seconds: e.target.value })}
                  data-testid="inbound-message-buffer-input"
                  placeholder="0"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono"
                />
              </label>
              <div className="text-xs text-carbon-500 self-end mb-2">
                Ejemplos: <code className="text-terracotta">0</code> = desactivado · <code className="text-terracotta">5</code> = 5 seg · <code className="text-terracotta">10</code> = 10 seg.
              </div>
            </div>
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">Supervisor · Escalamiento a humano</div>
            <h2 className="font-heading text-xl mb-2">Umbrales de escalamiento (0–100)</h2>
            <p className="text-sm text-carbon-500 mb-4">
              El Supervisor calcula un <b>Human Escalation Score</b> por conversación. <code className="text-terracotta">0–{(config.escalation_monitor_threshold ?? 40) - 1}</code> la IA sigue;
              <code className="text-terracotta"> {config.escalation_monitor_threshold ?? 40}–{(config.escalation_human_threshold ?? 70) - 1}</code> la IA sigue pero queda monitoreada;
              <code className="text-terracotta"> {config.escalation_human_threshold ?? 70}–100</code> se escala a un humano y la IA se pausa. Si el usuario pide una persona explícitamente, escala siempre.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="section-label">Umbral de monitoreo</span>
                <input type="number" min="0" max="100"
                  value={config.escalation_monitor_threshold ?? 40}
                  onChange={(e) => setConfig({ ...config, escalation_monitor_threshold: e.target.value })}
                  data-testid="escalation-monitor-input"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
              </label>
              <label className="block">
                <span className="section-label">Umbral de escalamiento a humano</span>
                <input type="number" min="0" max="100"
                  value={config.escalation_human_threshold ?? 70}
                  onChange={(e) => setConfig({ ...config, escalation_human_threshold: e.target.value })}
                  data-testid="escalation-human-input"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
              </label>
            </div>
          </Card>

          <Card className="p-6">
            <div className="section-label mb-2">Web Pública</div>
            <h2 className="font-heading text-xl mb-2">Contenido del sitio (CMS)</h2>
            <p className="text-sm text-carbon-500 mb-4">
              Edita imágenes, los módulos de "Y esto apenas comienza" y el carrusel editorial de la Home pública. Solo contenido: no afecta prompts, Journey ni reglas conversacionales.
            </p>
            <a href="/admin/settings/web" data-testid="open-webcms-link" className="inline-flex items-center gap-2 bg-carbon-900 text-white rounded px-4 py-2 text-sm font-medium">
              Abrir editor de contenido
            </a>
          </Card>

          <div>
            <button onClick={save} disabled={busy} data-testid="save-config-btn" className="bg-terracotta text-white px-5 py-2.5 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover disabled:opacity-60">
              <FloppyDisk size={14} /> Guardar configuración
            </button>
          </div>
        </div>
      </PageBody>
    </>
  );
}
